import { FC, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import MultiCarousel, { CarouselInternalState } from 'react-multi-carousel';
import { createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import { VirtualShelfItem } from '../useVirtualShelfFetch';
import { CarouselButtons, CarouselButtonsProps } from './CarouselButtons';
import { CarouselItem } from './CarouselItem';

import 'react-multi-carousel/lib/styles.css';
import './Carousel.scss';

interface CarouselProps {
  items: VirtualShelfItem[];
}

const cnBem = createBemBlockBuilder(['carousel']);
const responsiveConfig = {
  desktop: {
    breakpoint: { max: 3000, min: 1023 },
    items: 5,
  },
  tablet: {
    breakpoint: { max: 1023, min: 767 },
    items: 4,
  },
  phone: {
    breakpoint: { max: 767, min: 0 },
    items: 2,
  },
};

export const Carousel: FC<CarouselProps> = ({ items }) => {
  const { $t } = useIntl();
  const carouselRef = useRef<MultiCarousel>(null!);
  const [carouselState, setCarouselState] = useState<CarouselInternalState>(
    null!
  );

  return (
    <div
      className={cnBem('__wrapper')}
      role="region"
      aria-label={$t(
        {
          id: 'carousel.description',
          defaultMessage:
            '{visibleSlides, plural, one {Related items carousel. # visible slide of {totalItems} total} other {Related items carousel. # visible slides of {totalItems} total}}',
        },
        {
          visibleSlides: carouselState?.slidesToShow,
          totalItems: items.length,
        }
      )}
      data-auto="related-items-carousel"
    >
      <MultiCarousel
        keyBoardControl
        renderDotsOutside={false}
        className={cnBem()}
        responsive={responsiveConfig}
        draggable={false}
        arrows={false}
        shouldResetAutoplay={false}
        renderButtonGroupOutside
        ref={carouselRef}
        customButtonGroup={
          <CarouselButtons
            {...({
              setCarouselState,
            } as CarouselButtonsProps)}
          />
        }
      >
        {items.map(({ id, title, contributors, sourceTypes, isbns }, index) => {
          return (
            <CarouselItem
              key={id}
              id={id}
              index={index + 1}
              totalItems={items.length}
              title={title}
              contributors={contributors}
              sourceTypes={sourceTypes}
              isbns={isbns}
            />
          );
        })}
      </MultiCarousel>
    </div>
  );
};
